import { Table, Tag, Typography } from "antd";

/** What a delete does to a dependent record. */
export type DeleteImpact = "blocks" | "deleted" | "unlinked";

/** One record affected by a delete. Hrefs are app paths, opened in a new tab. */
export interface DeleteImpactRow {
	id: string;
	type: string;
	name: string;
	href: string;
	project?: { name: string; href: string };
	impact: DeleteImpact;
}

const IMPACT_TAG: Record<DeleteImpact, { color: string; label: string }> = {
	blocks: { color: "red", label: "Blocks delete" },
	deleted: { color: "orange", label: "Will be deleted" },
	unlinked: { color: "default", label: "Will be unlinked" },
};

// New tab, so the user keeps the dialog open while dealing with each dependent.
const NewTabLink = ({ href, label }: { href: string; label: string }) => (
	<Typography.Link
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		ellipsis
	>
		{label}
	</Typography.Link>
);

/**
 * Every record a delete touches and what happens to it, blockers first. Paged at 5 rows so the
 * surrounding dialog never grows.
 */
export function DeleteImpactTable({ rows }: { rows: DeleteImpactRow[] }) {
	const order: DeleteImpact[] = ["blocks", "deleted", "unlinked"];
	const sorted = [...rows].sort(
		(a, b) => order.indexOf(a.impact) - order.indexOf(b.impact),
	);
	const hasProject = rows.some((r) => r.project);
	return (
		<Table<DeleteImpactRow>
			size="small"
			rowKey="id"
			dataSource={sorted}
			pagination={{ pageSize: 5, size: "small", hideOnSinglePage: true }}
			columns={[
				{ title: "Type", dataIndex: "type", width: 120 },
				{
					title: "Name",
					ellipsis: true,
					render: (_, r) => <NewTabLink href={r.href} label={r.name} />,
				},
				...(hasProject
					? [
							{
								title: "Project",
								ellipsis: true,
								render: (_: unknown, r: DeleteImpactRow) =>
									r.project ? (
										<NewTabLink href={r.project.href} label={r.project.name} />
									) : (
										"-"
									),
							},
						]
					: []),
				{
					title: "Impact",
					width: 130,
					render: (_, r) => (
						<Tag color={IMPACT_TAG[r.impact].color}>
							{IMPACT_TAG[r.impact].label}
						</Tag>
					),
				},
			]}
		/>
	);
}
